/**
 * DSCSlotCard.jsx
 *
 * Per-slot card for the DSC: header title, line-drawing illustration,
 * dropdown(s) underneath, electric-blue chip + outline when filled.
 *
 * Citations (port-first):
 *  - Legacy LWC dscProductCard.html (full file): empty-product block lines
 *    1-13 (title + illustration); rod-image swap lines 74-77 (legacy toggles
 *    THRodImage vs THRodImage_Coupling when couplingRequired is on);
 *    filled state lines 200-289 (item code + attributes + dropdowns).
 *  - Plan SP1.5: data-state attribute pattern `[data-state="empty" |
 *    "filled" | "disabled"]`.
 *  - Brainstorm wishlist #10: visual fill-in state (empty outline ->
 *    electric-blue chip when filled).
 *
 * Visual states (Sandvik PWA template):
 *  - data-state="empty":   border + bg-paper, neutral border.
 *  - data-state="filled":  border-electric-blue + SlotChip in header.
 *  - data-state="disabled":upstream slot not yet picked.
 *
 * @module components/dsc/DSCSlotCard
 * @since 2026-05-20 (Group E SP2 — shared DSC primitives)
 */

import SlotChip from './SlotChip';

/**
 * @param {Object} props
 * @param {string} props.title — slot header (e.g. "TH Shanks", "Couplings").
 * @param {string} props.image — `/assets/dsc/...png` line-drawing src.
 * @param {string} [props.imageAlt] — defaults to title.
 * @param {string} [props.imageOverride] — caller-supplied alternative image
 *        path (used by TH Rods to swap to the rod+coupling variant when
 *        the coupling toggle is ON; mirrors legacy dscProductCard.html
 *        lines 74-77 if/else block).
 * @param {boolean} [props.isFilled] — controls the chip + outline state.
 * @param {boolean} [props.isDisabled] — upstream slot not yet picked.
 * @param {string} [props.filledLabel] — chip text when filled.
 * @param {() => void} [props.onClear] — clear handler for SlotChip.
 * @param {React.ReactNode} props.children — DSCDropdownRow(s) for this slot.
 * @param {string} [props.dataTestid]
 */
export default function DSCSlotCard({
  title,
  image,
  imageAlt,
  imageOverride,
  isFilled = false,
  isDisabled = false,
  filledLabel,
  onClear,
  children,
  dataTestid = 'dsc-slot-card',
}) {
  const state = isDisabled ? 'disabled' : isFilled ? 'filled' : 'empty';

  const stateClasses =
    state === 'filled'
      ? 'border border-electric-blue bg-bg-paper'
      : state === 'disabled'
      ? 'border border-n2 bg-n1 opacity-60'
      : 'border border-n3 bg-bg-paper';

  const resolvedImage = imageOverride || image;

  return (
    <div
      data-testid={dataTestid}
      data-state={state}
      className={`flex flex-col gap-3 p-4 ${stateClasses} rounded-none transition-colors`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-sandvik-headline font-normal text-base text-carbon-black flex-1">
          {title}
        </h3>
        {isFilled && filledLabel && (
          <SlotChip label={filledLabel} onClear={onClear} />
        )}
      </div>

      <div className="flex items-center justify-center min-h-[80px] py-2">
        {resolvedImage ? (
          <img
            src={resolvedImage}
            alt={imageAlt || title}
            title={title}
            className="max-h-20 max-w-full object-contain"
            data-testid="dsc-slot-image"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-16 bg-n1" aria-hidden="true" />
        )}
      </div>

      <div className="flex flex-col gap-3" data-testid="dsc-slot-dropdowns">
        {children}
      </div>
    </div>
  );
}
